/*
=========================================================
* Material Kit 2 PRO React - v2.1.1
=========================================================

* Product Page: https://www.creative-tim.com/product/material-kit-pro-react
* Copyright 2024 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// @mui material components
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";

// Material Kit 2 PRO React components
import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";

// Material Kit 2 PRO React examples
import DefaultNavbar from "examples/Navbars/DefaultNavbar";
import DefaultFooter from "components/DefaultFooter";

// Routes
import routes from "routes";
import footerRoutes from "footer.routes";

function Privacy() {
  const retentionData = [
    {
      purpose: "Account aanmaken en beheren",
      examples: "Accountgegevens",
      legalBasis: "Uitvoering van overeenkomst (art. 6(1)(b) AVG)",
      retention: "Levensduur van het account + 6 maanden na sluiting (back‑ups/juridisch)",
    },
    {
      purpose: "Chatdienst leveren",
      examples: "Prompts, outputs, bestanden, gebruik",
      legalBasis: "Uitvoering van overeenkomst (art. 6(1)(b) AVG)",
      retention: "In onze app 12 maanden; u kunt chats eerder zelf verwijderen",
    },
    {
      purpose: "Misbruik voorkomen, beveiligen, bugs verhelpen",
      examples: "Apparaat-/technische data, beperkte logs",
      legalBasis: "Gerechtvaardigd belang (art. 6(1)(f) AVG)",
      retention: "30–180 dagen voor logs",
    },
    {
      purpose: "Wettelijke verplichtingen",
      examples: "Facturen, wettelijk vereiste dossiers",
      legalBasis: "Wettelijke plicht (art. 6(1)(c) AVG)",
      retention: "Volgens de wet (bijv. 7 jaar voor belasting)",
    },
  ];

  return (
    <>
      <DefaultNavbar
        routes={routes}
        action={{ type: "internal", route: "/portaal", label: "portaal", color: "info" }}
        sticky
      />

      <MKBox component="section" pt={20} pb={12}>
        <Container>
          <Grid container justifyContent="center">
            <Grid item xs={12} lg={10}>
              <Card>
                <MKBox
                  variant="gradient"
                  bgColor="dark"
                  borderRadius="lg"
                  coloredShadow="dark"
                  p={3}
                  mt={-3}
                  mx={2}
                >
                  <MKTypography variant="h3" color="white">
                    Privacyverklaring
                  </MKTypography>
                  <MKTypography variant="body2" color="white" opacity={0.8}>
                    Verwerkingsverantwoordelijke: Willy Woef, KvK 22052216
                  </MKTypography>
                </MKBox>

                <MKBox pb={6} px={6}>
                  {/* 1. Wie wij zijn en reikwijdte */}
                  <MKTypography variant="h4" mt={6} mb={3}>
                    1. Wie wij zijn en reikwijdte
                  </MKTypography>
                  <MKTypography variant="body2" color="text" mb={3}>
                    Deze Privacyverklaring legt uit hoe wij persoonsgegevens verwerken wanneer u
                    onze AI Chat Platform en aanverwante diensten gebruikt. De Dienst is het zo
                    zorgvuldig beantwoorden van uw vraag over honden. Wij zijn de
                    <strong> verwerkingsverantwoordelijke</strong> voor gegevens die wij
                    rechtstreeks van u verzamelen om onze Dienst te leveren. Waar wij
                    gesprekgegevens naar onze AI‑provider (momenteel OpenAI) sturen om antwoorden te
                    genereren, blijven wij verwerkingsverantwoordelijke en treedt onze provider op
                    als
                    <strong> verwerker</strong>.
                  </MKTypography>

                  <Divider sx={{ my: 4 }} />

                  {/* 2. Welke gegevens wij verwerken */}
                  <MKTypography variant="h4" mt={4} mb={3}>
                    2. Welke gegevens wij verwerken
                  </MKTypography>
                  <MKTypography variant="body2" color="text" mb={2}>
                    <strong>Accountgegevens</strong> — naam, e‑mail, wachtwoordhash, instellingen.
                  </MKTypography>
                  <MKTypography variant="body2" color="text" mb={2}>
                    <strong>Servicedata</strong> — gespreks‑prompts &amp; antwoorden, geüploade
                    bestanden, tijdstempels, gebruikslimieten.
                  </MKTypography>
                  <MKTypography variant="body2" color="text" mb={2}>
                    <strong>Apparaat/techniek</strong> — IP‑adres, browser/apparaatinfo, logs voor
                    beveiliging en debugging.
                  </MKTypography>
                  <MKTypography variant="body2" color="text" mb={3}>
                    <strong>Cookies</strong> — alleen wat nodig is om u ingelogd en veilig te houden
                    (zie §8). Wij gebruiken uw gegevens <strong>niet</strong> voor marketing, tenzij
                    uitdrukkelijk vermeld.
                  </MKTypography>

                  <Divider sx={{ my: 4 }} />

                  {/* 3. Doelen, rechtsgronden en bewaartermijnen */}
                  <MKTypography variant="h4" mt={4} mb={3}>
                    3. Doelen, rechtsgronden en bewaartermijnen
                  </MKTypography>
                  <TableContainer component={Paper} sx={{ mb: 3 }}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>
                            <strong>Doel</strong>
                          </TableCell>
                          <TableCell>
                            <strong>Voorbeelden van gegevens</strong>
                          </TableCell>
                          <TableCell>
                            <strong>Rechtsgrond (AVG)</strong>
                          </TableCell>
                          <TableCell>
                            <strong>Bewaartermijn</strong>
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {retentionData.map((row, index) => (
                          <TableRow key={index}>
                            <TableCell>{row.purpose}</TableCell>
                            <TableCell>{row.examples}</TableCell>
                            <TableCell>{row.legalBasis}</TableCell>
                            <TableCell>{row.retention}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <MKTypography variant="body2" color="text" mb={3}>
                    Wij nemen <strong>geen</strong> besluiten die uitsluitend zijn gebaseerd op
                    geautomatiseerde verwerking die rechtsgevolgen voor u heeft of u anderszins in
                    aanmerkelijke mate treft.
                  </MKTypography>

                  <Divider sx={{ my: 4 }} />

                  {/* 4. Delen en internationale doorgiften */}
                  <MKTypography variant="h4" mt={4} mb={3}>
                    4. Delen en internationale doorgiften
                  </MKTypography>
                  <MKTypography variant="body2" color="text" mb={2}>
                    Wij delen gegevens met vertrouwde verwerkers om de Dienst te draaien:
                  </MKTypography>
                  <MKTypography variant="body2" color="text" mb={2}>
                    • <strong>AI‑provider:</strong> OpenAI — ontvangt uw prompts/bijlagen om
                    antwoorden te genereren.
                  </MKTypography>
                  <MKTypography variant="body2" color="text" mb={2}>
                    • <strong>Hosting &amp; infrastructuur:</strong> MongoDB, Strato.nl, Webdock.io
                    — host onze applicatie, databases en logs.
                  </MKTypography>

                  <MKBox sx={{ bgcolor: "grey.100", p: 3, borderRadius: 2, mb: 3 }}>
                    <MKTypography variant="body2" color="text">
                      <strong>OpenAI Gegevensbescherming:</strong> Wij sturen uw prompts, bijlagen
                      en metadata naar onze AI‑provider, OpenAI, uitsluitend om antwoorden voor u te
                      genereren. Wij hebben een verwerkersovereenkomst (DPA). Standaard bewaart de
                      OpenAI‑API input/output in logs tot maximaal 30 dagen voor misbruikmonitoring,
                      waarna deze wordt verwijderd. Wij configureren onze integratie voor
                      dataminimalisatie en staan niet toe dat uw data voor modeltraining wordt
                      gebruikt.
                    </MKTypography>
                  </MKBox>

                  <MKTypography variant="body2" color="text" mb={3}>
                    <strong>Doorgiften buiten de EER:</strong> bij doorgifte van gesprekgegevens aan
                    OpenAI in de VS steunen wij op het EU‑VS Data Privacy Framework en de
                    Standaardcontractbepalingen van de Europese Commissie onder onze
                    verwerkersovereenkomst, plus aanvullende waarborgen.
                  </MKTypography>

                  <Divider sx={{ my: 4 }} />

                  {/* 5. Uw rechten */}
                  <MKTypography variant="h4" mt={4} mb={3}>
                    5. Uw rechten
                  </MKTypography>
                  <MKTypography variant="body2" color="text" mb={3}>
                    Op grond van de AVG heeft u recht op <strong>inzage</strong>,
                    <strong> rectificatie</strong>, <strong>wissing</strong>,
                    <strong> beperking</strong>, <strong>overdraagbaarheid</strong> en
                    <strong> bezwaar</strong>. Waar verwerking op toestemming is gebaseerd, kunt u
                    deze te allen tijde intrekken. U oefent uw rechten uit via
                    <strong> privacy@aichatservices.nl</strong>. U heeft ook het recht een klacht in
                    te dienen bij de <strong>Autoriteit Persoonsgegevens (AP)</strong>.
                  </MKTypography>

                  <Divider sx={{ my: 4 }} />

                  {/* 6. Kinderen */}
                  <MKTypography variant="h4" mt={4} mb={3}>
                    6. Kinderen
                  </MKTypography>
                  <MKTypography variant="body2" color="text" mb={3}>
                    Onze Dienst is voor gebruikers van <strong>18+</strong>. Bent u jonger dan 18,
                    dan is toestemming van een ouder/voogd nodig. Indien wij constateren dat wij
                    gegevens van een kind zonder juiste toestemming hebben verzameld, verwijderen
                    wij die.
                  </MKTypography>

                  <Divider sx={{ my: 4 }} />

                  {/* 7. Beveiliging */}
                  <MKTypography variant="h4" mt={4} mb={3}>
                    7. Beveiliging
                  </MKTypography>
                  <MKTypography variant="body2" color="text" mb={3}>
                    Wij passen technische en organisatorische maatregelen toe (versleuteling tijdens
                    transport, toegangsbeheer, least‑privilege, back‑ups, audit logging). Geen enkel
                    systeem is 100% veilig.
                  </MKTypography>

                  <Divider sx={{ my: 4 }} />

                  {/* 8. Cookies */}
                  <MKTypography variant="h4" mt={4} mb={3}>
                    8. Cookies
                  </MKTypography>
                  <MKTypography variant="body2" color="text" mb={3}>
                    Wij gebruiken <strong>strikt noodzakelijke</strong> cookies voor inloggen,
                    sessiebeveiliging en voorkeuren. Voor deze cookies is geen toestemming vereist
                    onder NL/EU‑regels, maar wij lichten ze hieronder toe. Als wij later analytics
                    of tracking toevoegen, vragen wij om uw toestemming via een duidelijke banner
                    met granulaire keuzes.
                  </MKTypography>

                  <Divider sx={{ my: 4 }} />

                  {/* 9. Bewaren */}
                  <MKTypography variant="h4" mt={4} mb={3}>
                    9. Bewaren
                  </MKTypography>
                  <MKTypography variant="body2" color="text" mb={3}>
                    Wij bewaren uw gegevens niet langer dan nodig voor de doelen hierboven.
                    Standaard bewaren wij gesprekshistorie <strong>12 maanden</strong>, waarna wij
                    deze verwijderen of anonimiseren, tenzij wij deze langer moeten bewaren (bijv.
                    ter naleving van wetgeving of voor geschiloplossing). U kunt chats op ieder
                    moment zelf verwijderen.
                  </MKTypography>

                  <Divider sx={{ my: 4 }} />

                  {/* 10. Leveranciers & beleid */}
                  <MKTypography variant="h4" mt={4} mb={3}>
                    10. Leveranciers &amp; beleid (transparantie‑links)
                  </MKTypography>
                  <MKTypography variant="body2" color="text" mb={2}>
                    • DPA en datacontroles van onze AI‑provider:{" "}
                    <strong>OpenAI Data Processing Addendum</strong>
                  </MKTypography>
                  <MKTypography variant="body2" color="text" mb={2}>
                    • Onze lijst met verwerkers/subverwerkers: Beschikbaar op aanvraag
                  </MKTypography>
                  <MKTypography variant="body2" color="text" mb={3}>
                    • Export van uw gegevens: Via uw accountinstellingen of per e‑mail aanvraag
                  </MKTypography>

                  <Divider sx={{ my: 4 }} />

                  {/* 11. Wijzigingen */}
                  <MKTypography variant="h4" mt={4} mb={3}>
                    11. Wijzigingen in deze verklaring
                  </MKTypography>
                  <MKTypography variant="body2" color="text" mb={3}>
                    Wij plaatsen wijzigingen op deze pagina en, bij materiële wijzigingen,
                    informeren wij u per e‑mail of in de app. Controleer regelmatig.
                  </MKTypography>

                  <Divider sx={{ my: 4 }} />

                  {/* 12. Contact */}
                  <MKTypography variant="h4" mt={4} mb={3}>
                    12. Contact
                  </MKTypography>
                  <MKTypography variant="body2" color="text" mb={2}>
                    <strong>Verwerkingsverantwoordelijke:</strong> Willy Woef, KvK 22052216
                  </MKTypography>
                  <MKTypography variant="body2" color="text" mb={3}>
                    <strong>E‑mailadres:</strong> info@willy-woef.nl
                  </MKTypography>
                </MKBox>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </MKBox>

      <MKBox pt={6} px={1} mt={6}>
        <DefaultFooter content={footerRoutes} />
      </MKBox>
    </>
  );
}

export default Privacy;
